import { ROLE_TYPES, ROOT_TEAM_ID } from '@scio3/shared-types';

/**
 * PROTOTYPE fixtures — Kati's To-Be user-group model, mocked locally.
 *
 * Nothing here talks to Apollo. The whole point of the mock is to argue about the SHAPE of the
 * create-group flow (required teamspace pairing, folder grants nested under their teamspace,
 * Open/Closed teamspaces) before any of it is specified for build. See
 * `~/.claude/plans/alright-for-this-branch-woolly-waffle.md`.
 *
 * Vocabulary trap, restated because the codebase is genuinely ambiguous here:
 *   Team == Workspace == Teamspace  → ONE entity (`teams`, `teamId`). "Teamspace" is the UI word.
 *   Group                            → (a) a user group (`userGroups`) — what we create here, or
 *                                      (b) a FOLDER (the folder GraphQL type is literally `Group`).
 * Below, `MockFolder` is (b) and `MockTeamspace` is the Team.
 */

// The five independent folder trees. A folder lives in exactly one of them, which is why creating
// a folder during group creation has to ask which type(s) it lands in.
export const FOLDER_TYPES = [
  { value: 'asset', label: 'Assets' },
  { value: 'device', label: 'Screens' },
  { value: 'playlist', label: 'Playlists' },
  { value: 'schedule', label: 'Schedules' },
  { value: 'engage', label: 'Engage' },
] as const;

export type FolderType = (typeof FOLDER_TYPES)[number]['value'];

/**
 * Open  → every folder created in this teamspace defaults to "Everyone on this teamspace" ('0').
 * Closed→ every folder created in this teamspace defaults to "Only admins" ('-1'), so a member
 *         added to the teamspace sees no folder until a group grants them one.
 *
 * Decided 2026-08-24: Closed means NO FOLDER is visible until granted. Root-level (unfiled)
 * content is out of scope and behaves exactly as it does today, because folder permissions do
 * not reach it.
 */
export type TeamspaceAccessMode = 'open' | 'closed';

export interface MockTeamspace {
  id: string;
  name: string;
  description?: string;
  accessMode: TeamspaceAccessMode;
  /** The account root ('1'). Its group grants live on companies.groups[], not teams.groups[]. */
  isDefault?: boolean;
}

export interface MockRole {
  id: string;
  label: string;
  /** Mirrors systemRoles.ts: Billing + Read Only are content read-only. Drives the Edit cap. */
  allowsEdit: boolean;
  isCustom?: boolean;
}

export interface MockUser {
  id: string;
  name: string;
  email: string;
}

export interface MockFolder {
  id: string;
  teamspaceId: string;
  type: FolderType;
  name: string;
  /** Parent path, matching the real folder doc (`path` is the PARENT, not the folder itself). */
  path: string;
}

// --- Teamspaces ----------------------------------------------------------------------------------

export const MOCK_TEAMSPACES: MockTeamspace[] = [
  { id: ROOT_TEAM_ID, name: 'Main Teamspace', accessMode: 'open', isDefault: true },
  { id: 't-marketing', name: 'Marketing', description: 'Brand and campaign content', accessMode: 'open' },
  { id: 't-retail', name: 'Retail Ops', description: 'Store-facing screens', accessMode: 'open' },
  { id: 't-hr', name: 'HR', description: 'Internal comms', accessMode: 'closed' },
  { id: 't-franchise', name: 'Franchisees', description: 'External operators', accessMode: 'closed' },
];

// --- Roles ---------------------------------------------------------------------------------------

// Matches SCOPED_SYSTEM_ROLES in CreateUserGroupDialog: Owner is never offered on a group
// (rejected server-side as OWNER_ROLE_NOT_ALLOWED_ON_GROUP), and Content Proposal/Approval are
// soft-deprecated (D7) so they are dropped from the picker.
export const MOCK_ROLES: MockRole[] = [
  { id: ROLE_TYPES.ADMIN, label: 'Admin', allowsEdit: true },
  { id: ROLE_TYPES.USER, label: 'User', allowsEdit: true },
  { id: ROLE_TYPES.BILLING, label: 'Billing', allowsEdit: false },
  { id: ROLE_TYPES.READ_ONLY, label: 'Read Only', allowsEdit: false },
  { id: 'custom-store-manager', label: 'Store Manager', allowsEdit: true, isCustom: true },
  { id: 'custom-viewer-plus', label: 'Viewer Plus', allowsEdit: false, isCustom: true },
];

// --- Users ---------------------------------------------------------------------------------------

const NAMES = [
  'Ava Bennett', 'Marcus Hale', 'Priya Raghavan', 'Tom Okonkwo', 'Lena Fischer',
  'Diego Santos', 'Nora Whitfield', 'Sam Iverson', 'Yuki Tanaka', 'Rachel Adeyemi',
  'Owen Brady', 'Zara Malik', 'Caleb Nguyen', 'Ingrid Sorensen', 'Felix Duarte',
  'Maya Lindqvist', 'Jonah Reyes', 'Simone Attah', 'Victor Kovacs', 'Hana Petrova',
];

export const MOCK_USERS: MockUser[] = NAMES.map((name, i) => ({
  id: `u-${i + 1}`,
  name,
  email: `${name.toLowerCase().replace(/[^a-z]+/g, '.')}@example.com`,
}));

// --- Folders -------------------------------------------------------------------------------------

let folderSeq = 0;
const folder = (teamspaceId: string, type: FolderType, path: string, name: string): MockFolder => ({
  id: `f-${++folderSeq}`,
  teamspaceId,
  type,
  name,
  path,
});

// Deliberately uneven: some teamspaces are folder-rich, HR has almost nothing, and a couple of
// trees nest two levels so the picker has to show a real path rather than a bare name.
export const MOCK_FOLDERS: MockFolder[] = [
  // Main Teamspace — the central library pattern (segment C: content built centrally, handed out)
  folder(ROOT_TEAM_ID, 'asset', '', 'Brand Library'),
  folder(ROOT_TEAM_ID, 'asset', '/Brand Library', 'Logos'),
  folder(ROOT_TEAM_ID, 'asset', '/Brand Library', 'Seasonal'),
  folder(ROOT_TEAM_ID, 'asset', '', 'Corporate Comms'),
  folder(ROOT_TEAM_ID, 'device', '', 'Head Office'),
  folder(ROOT_TEAM_ID, 'playlist', '', 'Always On'),
  folder(ROOT_TEAM_ID, 'schedule', '', 'Standard Hours'),

  // Marketing
  folder('t-marketing', 'asset', '', 'Campaigns'),
  folder('t-marketing', 'asset', '/Campaigns', 'Spring 2026'),
  folder('t-marketing', 'asset', '/Campaigns', 'Holiday 2026'),
  folder('t-marketing', 'asset', '', 'Social Cuts'),
  folder('t-marketing', 'playlist', '', 'Campaign Loops'),
  folder('t-marketing', 'device', '', 'Showroom Screens'),
  folder('t-marketing', 'engage', '', 'Lobby Engage'),

  // Retail Ops
  folder('t-retail', 'device', '', 'Store 001'),
  folder('t-retail', 'device', '', 'Store 042'),
  folder('t-retail', 'device', '', 'Store 118'),
  folder('t-retail', 'device', '/Store 042', 'Checkout Lane'),
  folder('t-retail', 'asset', '', 'Promo Cards'),
  folder('t-retail', 'playlist', '', 'Store Loop'),
  folder('t-retail', 'schedule', '', 'Trading Hours'),

  // HR — sparse on purpose: a Closed teamspace with almost nothing to grant is exactly the
  // "6,695 users land on an empty screen" case from the migration analysis.
  folder('t-hr', 'asset', '', 'Policies'),

  // Franchisees — Closed, one folder per operator
  folder('t-franchise', 'asset', '', 'Operator Pack'),
  folder('t-franchise', 'device', '', 'Operator Screens'),
];

/** Full display path for a folder, matching HomeFoldersEditor's `${path}/${name}` convention. */
export const folderFullPath = (f: { path: string; name: string }) =>
  f.path ? `${f.path}/${f.name}` : `/${f.name}`;

export const teamspaceById = (id: string) => MOCK_TEAMSPACES.find((w) => w.id === id);
export const roleById = (id: string) => MOCK_ROLES.find((r) => r.id === id);
export const folderTypeLabel = (type: FolderType) =>
  FOLDER_TYPES.find((ft) => ft.value === type)?.label ?? type;
